import { z } from "zod";

/** Audience controls belong to the story's delivery language, independently of the editor UI. */
export const STORY_DELIVERY_TEXT = {
  continue: "Continue",
  reveal: "Reveal",
  next: "Next",
  play: "Play",
  pause: "Pause",
  resume: "Resume",
  replay: "Replay",
  preparing: "Preparing",
  waitingResponse: "Waiting for your response",
  respond: "Respond",
  respondFor: "Respond for {name}",
  maybeLater: "Maybe later",
  skipAndContinue: "Skip and continue",
  sayYourWay: "Or say it your own way...",
  sayIt: "Say it",
  sendResponse: "Send response",
  letCharacterSay: "Let {name} say it",
  yourResponse: "Your response",
  suggestedResponses: "Suggested responses",
  suggestedWords: "Suggested words",
  typeResponse: "Type your response…",
  wordsFor: "Words for {name} to say…",
  characterWords: "{name}’s words",
  characterResponse: "{name}’s response",
  closeResponse: "Close response",
  speakingFor: "Speaking for {name}",
  choose: "Choose",
  chooseFor: "Choose for {name}",
  yourChoice: "Your choice",
  choicesFor: "Choices for {name}",
  signingIn: "Signing in…",
  signInToContinue: "Sign in to continue",
  signInToChoose: "Sign in to choose",
  continuingScene: "Continuing the scene…",
  responseError: "The scene could not continue. Your draft is still here. Try again.",
  choiceError: "The scene could not continue. Choose again to retry.",
  signInError: "Sign in did not finish. You can try again.",
  replyUnavailable: "Reply unavailable",
  close: "Close",
  character: "the character",
} as const;
export type StoryDeliveryTextKey = keyof typeof STORY_DELIVERY_TEXT;
export const STORY_DELIVERY_TEXT_KEYS = Object.keys(STORY_DELIVERY_TEXT) as [
  StoryDeliveryTextKey,
  ...StoryDeliveryTextKey[],
];
export const zStoryDeliveryLabels = z.record(z.enum(STORY_DELIVERY_TEXT_KEYS), z.string().trim().min(1).max(500));
export const zStoryDelivery = z.object({
  voice: z.boolean(),
  language: z.string().max(100),
  labels: zStoryDeliveryLabels.optional(),
});
export type StoryDelivery = z.infer<typeof zStoryDelivery>;

const spanish: Record<StoryDeliveryTextKey, string> = {
  continue: "Continuar",
  reveal: "Revelar",
  next: "Siguiente",
  play: "Reproducir",
  pause: "Pausar",
  resume: "Reanudar",
  replay: "Repetir",
  preparing: "Preparando",
  waitingResponse: "Esperando tu respuesta",
  respond: "Responder",
  respondFor: "Responder por {name}",
  maybeLater: "Quizás más tarde",
  skipAndContinue: "Saltar y continuar",
  sayYourWay: "O dilo a tu manera…",
  sayIt: "Decirlo",
  sendResponse: "Enviar respuesta",
  letCharacterSay: "Que {name} lo diga",
  yourResponse: "Tu respuesta",
  suggestedResponses: "Respuestas sugeridas",
  suggestedWords: "Palabras sugeridas",
  typeResponse: "Escribe tu respuesta…",
  wordsFor: "Palabras para que {name} las diga…",
  characterWords: "Palabras de {name}",
  characterResponse: "Respuesta de {name}",
  closeResponse: "Cerrar respuesta",
  speakingFor: "Hablando por {name}",
  choose: "Elegir",
  chooseFor: "Elegir por {name}",
  yourChoice: "Tu elección",
  choicesFor: "Opciones para {name}",
  signingIn: "Iniciando sesión…",
  signInToContinue: "Inicia sesión para continuar",
  signInToChoose: "Inicia sesión para elegir",
  continuingScene: "Continuando la escena…",
  responseError: "No se pudo continuar la escena. Tu borrador sigue aquí. Inténtalo de nuevo.",
  choiceError: "No se pudo continuar la escena. Elige de nuevo para intentarlo.",
  signInError: "No se completó el inicio de sesión. Puedes intentarlo de nuevo.",
  replyUnavailable: "Respuesta no disponible",
  close: "Cerrar",
  character: "el personaje",
};
export function storyDeliveryText(delivery: StoryDelivery | undefined, key: StoryDeliveryTextKey, name = ""): string {
  const text =
    delivery?.labels?.[key] || (/^es(?:-|$)/i.test(delivery?.language || "") ? spanish[key] : STORY_DELIVERY_TEXT[key]);
  return text.replace(/\{name\}/g, name);
}
export function storyContinueLabel(delivery: StoryDelivery | undefined, label?: string): string {
  return label === "Reveal"
    ? storyDeliveryText(delivery, "reveal")
    : !label || label === "Continue" || label === "Next"
      ? storyDeliveryText(delivery, label === "Next" ? "next" : "continue")
      : label;
}
